import {
    BaseQueryFn,
    createApi,
    FetchArgs,
    fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { ReportResponse } from "@/types/ReportResponse.ts";
import { ReportRequest } from "@/types/generated/ReportRequest";
import { ReportPerformanceRequest } from "@/types/generated/ReportPerformanceRequest";
import { ReportPerformanceResponse } from "@/types/generated/ReportPerformanceResponse";
import { ReportInfluenceRequest } from "@/types/generated/ReportInfluenceRequest";
import { ReportInfluenceResponse } from "@/types/generated/ReportInfluenceResponse";
import { ReportInfluenceBucketDetailRequest } from "@/types/generated/ReportInfluenceBucketDetailRequest";
import { ReportInfluenceBucketDetailResponse } from "@/types/generated/ReportInfluenceBucketDetailResponse";
import { ReportInfluenceJourneyRequest } from "@/types/generated/ReportInfluenceJourneyRequest";
import { ReportInfluenceJourneyResponse } from "@/types/generated/ReportInfluenceJourneyResponse";
import { ReportLinkedinReachedRolesRequest } from "@/types/generated/ReportLinkedinReachedRolesRequest";
import { ReportLinkedinReachedRolesResponse } from "@/types/generated/ReportLinkedinReachedRolesResponse";
import { ReportLinkedinSpendByFitRequest } from "@/types/generated/ReportLinkedinSpendByFitRequest";
import { ReportLinkedinSpendByFitResponse } from "@/types/generated/ReportLinkedinSpendByFitResponse";
import { ReportLinkedinSpendCompaniesRequest } from "@/types/generated/ReportLinkedinSpendCompaniesRequest";
import { ReportLinkedinSpendCompaniesResponse } from "@/types/generated/ReportLinkedinSpendCompaniesResponse";
import { ReportLinkedinTopCompaniesRequest } from "@/types/generated/ReportLinkedinTopCompaniesRequest";
import { ReportLinkedinTopCompaniesResponse } from "@/types/generated/ReportLinkedinTopCompaniesResponse";
import { ReportPaidAdsTopCompaniesRequest } from "@/types/generated/ReportPaidAdsTopCompaniesRequest";
import { ReportPaidAdsTopCompaniesResponse } from "@/types/generated/ReportPaidAdsTopCompaniesResponse";
import { ContactsResponse } from "@/types/generated/ContactsResponse.ts";
import { ContactsRequest } from "@/types/generated/ContactsRequest";
import { ContactResponse } from "@/types/generated/ContactResponse.ts";
import { ContactRequest } from "@/types/generated/ContactRequest.ts";
import { WorkspaceMetaRequest } from "@/types/generated/WorkspaceMetaRequest.ts";
import { WorkspaceMetaResponse } from "@/types/generated/WorkspaceMetaResponse.ts";
import { LensGeographyRequest } from "@/types/generated/LensGeographyRequest.ts";
import { LensGeographyResponse } from "@/types/generated/LensGeographyResponse.ts";
import { fetchAuthSession } from "aws-amplify/auth";
import { RootState } from "@/app/store.ts";
import { getSelectedWorkspace } from "@/features/Workspace/Workspace.slice.ts";
import { DashboardWidgetsResponse } from "@/types/generated/DashboardWidgetsResponse.ts";
import { DataMaturityRequest } from "@/types/generated/DataMaturityRequest.ts";
import { DataMaturityResponse } from "@/types/generated/DataMaturityResponse.ts";
import { WidgetsRequest } from "@/types/generated/WidgetsRequest.ts";
import { FunnelResponse } from "@/types/generated/FunnelResponse.ts";
import { FunnelRequest } from "@/types/generated/FunnelRequest.ts";
import { toast } from "react-hot-toast";
import {
    ApiError,
    apiErrorFromException,
    normalizeApiError,
} from "@/services/apis/apiError.ts";
import { CompaniesResponse } from "@/types/generated/CompaniesResponse";
import { CompaniesRequest } from "@/types/generated/CompaniesRequest.ts";
import { CompanyResponse } from "@/types/generated/CompanyResponse.ts";
import { CompanyRequest } from "@/types/generated/CompanyRequest.ts";
import { JourneySessionResponse } from "@/types/generated/JourneySessionResponse";
import { JourneySessionRequest } from "@/types/generated/JourneySessionRequest.ts";
import { WorkspaceDataResponse } from "@/types/generated/WorkspaceDataResponse.ts";
import { WorkspaceDataRequest } from "@/types/generated/WorkspaceDataRequest.ts";
import { OutcomeDetailResponse } from "@/types/generated/OutcomeDetailResponse";
import { OutcomeDetailRequest } from "@/types/generated/OutcomeDetailRequest";
import { ReportPerformanceBucketDetailRequest } from "@/types/generated/ReportPerformanceBucketDetailRequest";
import { ReportPerformanceBucketDetailResponse } from "@/types/generated/ReportPerformanceBucketDetailResponse";
import { ReportPerformanceBucketVisitsRequest } from "@/types/generated/ReportPerformanceBucketVisitsRequest";
import { ReportPerformanceBucketVisitsResponse } from "@/types/generated/ReportPerformanceBucketVisitsResponse";
import { FunnelStepDetailRequest } from "@/types/generated/FunnelStepDetailRequest";
import { DealsRequest } from "@/types/generated/DealsRequest";
import { DealsResponse } from "@/types/generated/DealsResponse";
import { TransactionsRequest } from "@/types/generated/TransactionsRequest";
import { TransactionsResponse } from "@/types/generated/TransactionsResponse";
import { ReportAudienceRequest } from "@/types/generated/ReportAudienceRequest";
import { ReportAudienceResponse } from "@/types/generated/ReportAudienceResponse";
import { ReportAudienceVelocityRequest } from "@/types/generated/ReportAudienceVelocityRequest";
import { ReportAudienceVelocityResponse } from "@/types/generated/ReportAudienceVelocityResponse";
import { ReportTaggingUpliftRequest } from "@/types/generated/ReportTaggingUpliftRequest";
import { ReportTaggingUpliftResponse } from "@/types/generated/ReportTaggingUpliftResponse";
import { ReportTrackingPlanArrivalsRequest } from "@/types/generated/ReportTrackingPlanArrivalsRequest";
import { ReportTrackingPlanArrivalsResponse } from "@/types/generated/ReportTrackingPlanArrivalsResponse";

const baseQueryFactory = (baseUrl: string) =>
    fetchBaseQuery({
        baseUrl,
        prepareHeaders: async (headers, { getState }) => {
            const { tokens } = await fetchAuthSession();

            if (!tokens?.accessToken) {
                throw new Error("No access token");
            }

            const state = getState() as RootState;
            const workspaceId = getSelectedWorkspace(state)?.id;
            if (workspaceId) {
                headers.set("Spectacle-Workspace-Id", workspaceId);
            }
            headers.set("authorization", `Bearer ${tokens.accessToken}`);
            headers.set("Content-Type", "application/json");

            return headers;
        },
    });

function getBaseQuery(
    baseUrl: string,
): BaseQueryFn<FetchArgs, unknown, ApiError> {
    const baseQuery = baseQueryFactory(baseUrl);
    return async (args, api, extraOptions) => {
        let result: Awaited<ReturnType<typeof baseQuery>>;
        try {
            result = await baseQuery(args, api, extraOptions);
        } catch (error) {
            result = { error: apiErrorFromException(error) };
        }

        if (result.error) {
            const error = normalizeApiError(result.error);

            const message =
                error.message ||
                "An unknown error occurred. Please try again. If the problem persists, please contact support";
            toast.error(message);

            return { ...result, error };
        }

        return result;
    };
}

// Define a service using a base URL and expected endpoints
export const dataApi = createApi({
    reducerPath: "dataApi",
    baseQuery: getBaseQuery(import.meta.env.VITE_DATA_API_URL),
    tagTypes: [
        "Report",
        "ReportPerformance",
        "ReportPerformanceBucketDetail",
        "ReportLinkedin",
        "ReportInfluenceBucketDetail",
        "ReportInfluenceJourney",
        "ReportAudience",
        "ReportAudienceVelocity",
        "ReportTaggingUplift",
        "ReportTrackingPlanArrivals",
        "OutcomeDetail",
        "Contacts",
        "Contact",
        "Companies",
        "Company",
        "Deals",
        "Transactions",
        "WorkspaceMeta",
        "DashboardWidgets",
        "Funnel",
        "FunnelStepDetail",
        "JourneySession",
        "WorkspaceData",
    ],
    endpoints: (builder) => {
        const createEndpoint = <Response, Request>(
            operationName: string,
            tags: readonly (
                | "Report"
                | "OutcomeDetail"
                | "ReportPerformance"
                | "ReportPerformanceBucketDetail"
                | "ReportLinkedin"
                | "ReportInfluenceBucketDetail"
                | "ReportInfluenceJourney"
                | "ReportAudience"
                | "ReportAudienceVelocity"
                | "ReportTaggingUplift"
                | "ReportTrackingPlanArrivals"
                | "Contacts"
                | "Contact"
                | "Companies"
                | "Company"
                | "Deals"
                | "Transactions"
                | "WorkspaceMeta"
                | "DashboardWidgets"
                | "Funnel"
                | "FunnelStepDetail"
                | "JourneySession"
                | "WorkspaceData"
            )[] = [],
        ) =>
            builder.query<Response, Request>({
                query: (request) => ({
                    url: "operation",
                    method: "POST",
                    body: { operation: { [operationName]: request } },
                }),
                providesTags: tags,
            });

        // Define the endpoint configurations
        return {
            getReport: builder.query<ReportResponse, ReportRequest>({
                query: (request) => ({
                    url: "operation",
                    method: "POST",
                    body: { operation: { GetReport: request } },
                }),
                providesTags: ["Report"],
                transformResponse: (
                    response: ReportResponse,
                ): ReportResponse => ({
                    ...response,
                    chartData: response.chartData ?? null,
                }),
            }),
            getReportPerformance: createEndpoint<
                ReportPerformanceResponse,
                ReportPerformanceRequest
            >("GetReportPerformance", ["ReportPerformance"]),
            getReportPerformanceBucketDetail: createEndpoint<
                ReportPerformanceBucketDetailResponse,
                ReportPerformanceBucketDetailRequest
            >("GetReportPerformanceBucketDetail", [
                "ReportPerformanceBucketDetail",
            ]),
            // The visits behind a report row's Visits cell.
            getReportPerformanceBucketVisits: createEndpoint<
                ReportPerformanceBucketVisitsResponse,
                ReportPerformanceBucketVisitsRequest
            >("GetReportPerformanceBucketVisits"),
            getReportInfluence: createEndpoint<
                ReportInfluenceResponse,
                ReportInfluenceRequest
            >("GetReportInfluence", ["ReportLinkedin"]),
            // Influence drill-down: lists the influenced deals/revenue events
            // behind a clicked total or chart bar, with the influenced company.
            getReportInfluenceBucketDetail: createEndpoint<
                ReportInfluenceBucketDetailResponse,
                ReportInfluenceBucketDetailRequest
            >("GetReportInfluenceBucketDetail", [
                "ReportInfluenceBucketDetail",
            ]),
            getReportInfluenceJourney: createEndpoint<
                ReportInfluenceJourneyResponse,
                ReportInfluenceJourneyRequest
            >("GetReportInfluenceJourney", ["ReportInfluenceJourney"]),
            getReportAudience: createEndpoint<
                ReportAudienceResponse,
                ReportAudienceRequest
            >("GetReportAudience", ["ReportAudience"]),
            getReportAudienceVelocity: createEndpoint<
                ReportAudienceVelocityResponse,
                ReportAudienceVelocityRequest
            >("GetReportAudienceVelocity", ["ReportAudienceVelocity"]),
            // Server-side tracking uplift (SPE-1208). Behind the
            // `server-side-tracking` flag on the routes that call it.
            getReportTaggingUplift: createEndpoint<
                ReportTaggingUpliftResponse,
                ReportTaggingUpliftRequest
            >("GetReportTaggingUplift", ["ReportTaggingUplift"]),
            // The tracking plan's arrivals: each planned event's daily count
            // out of the same fold, for the plan table's sparkline.
            getReportTrackingPlanArrivals: createEndpoint<
                ReportTrackingPlanArrivalsResponse,
                ReportTrackingPlanArrivalsRequest
            >("GetReportTrackingPlanArrivals", ["ReportTrackingPlanArrivals"]),
            getReportLinkedinSpendByFit: createEndpoint<
                ReportLinkedinSpendByFitResponse,
                ReportLinkedinSpendByFitRequest
            >("GetReportLinkedinSpendByFit", ["ReportLinkedin"]),
            // The same estimate per reached company, most expensive first.
            getReportLinkedinReachedRoles: createEndpoint<
                ReportLinkedinReachedRolesResponse,
                ReportLinkedinReachedRolesRequest
            >("GetReportLinkedinReachedRoles", ["ReportLinkedin"]),
            getReportLinkedinSpendCompanies: createEndpoint<
                ReportLinkedinSpendCompaniesResponse,
                ReportLinkedinSpendCompaniesRequest
            >("GetReportLinkedinSpendCompanies", ["ReportLinkedin"]),
            getReportLinkedinTopCompanies: createEndpoint<
                ReportLinkedinTopCompaniesResponse,
                ReportLinkedinTopCompaniesRequest
            >("GetReportLinkedinTopCompanies", ["ReportLinkedin"]),
            getReportPaidAdsTopCompanies: createEndpoint<
                ReportPaidAdsTopCompaniesResponse,
                ReportPaidAdsTopCompaniesRequest
            >("GetReportPaidAdsTopCompanies", ["ReportLinkedin"]),
            getContacts: createEndpoint<ContactsResponse, ContactsRequest>(
                "GetContacts",
                ["Contacts"],
            ),
            getContact: createEndpoint<ContactResponse, ContactRequest>(
                "GetContact",
                ["Contact"],
            ),
            getCompanies: createEndpoint<CompaniesResponse, CompaniesRequest>(
                "GetCompanies",
                ["Companies"],
            ),
            getCompany: createEndpoint<CompanyResponse, CompanyRequest>(
                "GetCompany",
                ["Company"],
            ),
            getDeals: createEndpoint<DealsResponse, DealsRequest>("GetDeals", [
                "Deals",
            ]),
            getTransactions: createEndpoint<
                TransactionsResponse,
                TransactionsRequest
            >("GetTransactions", ["Transactions"]),
            getWorkspaceMeta: createEndpoint<
                WorkspaceMetaResponse,
                WorkspaceMetaRequest
            >("GetWorkspaceMeta", ["WorkspaceMeta"]),
            // The share of the last 30 days' anonymous visitors Lens could
            // resolve to a contact (United States only), for the dialog that
            // switches contacts on.
            getLensGeography: createEndpoint<
                LensGeographyResponse,
                LensGeographyRequest
            >("GetLensGeography"),
            getDashboardWidgets: createEndpoint<
                DashboardWidgetsResponse,
                WidgetsRequest
            >("GetWidgets", ["DashboardWidgets"]),
            // How developed a period's figures are: a recent period's leads
            // have had less time to pay than an older one's.
            getDataMaturity: createEndpoint<
                DataMaturityResponse,
                DataMaturityRequest
            >("GetDataMaturity"),
            getFunnel: createEndpoint<FunnelResponse, FunnelRequest>(
                "GetFunnel",
                ["Funnel"],
            ),
            // Funnel step drill-down: lists the contacts/companies that reached a
            // clicked step within a dimension value. Reuses the reporting bucket-detail
            // row shape so the dialog and "lead" outcome-detail click-through are shared.
            getFunnelStepDetail: createEndpoint<
                ReportPerformanceBucketDetailResponse,
                FunnelStepDetailRequest
            >("GetFunnelStepDetail", ["FunnelStepDetail"]),
            getJourneySession: createEndpoint<
                JourneySessionResponse,
                JourneySessionRequest
            >("GetJourneySession", ["JourneySession"]),
            getWorkspaceData: createEndpoint<
                WorkspaceDataResponse,
                WorkspaceDataRequest
            >("GetWorkspaceData", ["WorkspaceData"]),
            getOutcomeDetail: createEndpoint<
                OutcomeDetailResponse,
                OutcomeDetailRequest
            >("GetOutcomeDetail", ["OutcomeDetail"]),
        };
    },
});

export const {
    useLazyGetReportQuery,
    useGetReportQuery,
    useLazyGetContactsQuery,
    useGetContactQuery,
    useGetWorkspaceMetaQuery,
    useGetLensGeographyQuery,
    useGetDashboardWidgetsQuery,
    useGetDataMaturityQuery,
    useGetFunnelQuery,
    useLazyGetFunnelQuery,
    useGetFunnelStepDetailQuery,
    useGetCompaniesQuery,
    useLazyGetCompaniesQuery,
    useGetCompanyQuery,
    useGetDealsQuery,
    useLazyGetDealsQuery,
    useLazyGetTransactionsQuery,
    useGetJourneySessionQuery,
    useGetReportPerformanceQuery,
    useGetReportPerformanceBucketDetailQuery,
    useLazyGetReportPerformanceBucketVisitsQuery,
    useGetReportInfluenceQuery,
    useLazyGetReportInfluenceBucketDetailQuery,
    useGetReportInfluenceJourneyQuery,
    useGetReportAudienceQuery,
    useGetReportAudienceVelocityQuery,
    useGetReportTaggingUpliftQuery,
    useGetReportTrackingPlanArrivalsQuery,
    useGetReportLinkedinSpendByFitQuery,
    useGetReportLinkedinReachedRolesQuery,
    useGetReportLinkedinSpendCompaniesQuery,
    useGetReportLinkedinTopCompaniesQuery,
    useLazyGetReportLinkedinTopCompaniesQuery,
    useGetReportPaidAdsTopCompaniesQuery,
    useLazyGetReportPaidAdsTopCompaniesQuery,
    useLazyGetWorkspaceDataQuery,
    useGetOutcomeDetailQuery,
} = dataApi;
